import { BlockAPI } from '@editorjs/editorjs';
import { ActivityBlockConfig } from '..';
import QuizDomFactory, { QuizMessageVariant } from '../factory/quiz-factory';
import Alternative, { AlternativeStatus } from '../model/alternative';
import Quiz from '../model/quiz';
import { mountAlternativeId } from '../utils/alternative';

export const CHECK_ANSWER_ERROR_MESSAGE =
  'We could not check your answer. Try again.';
export const FEEDBACK_UNAVAILABLE_MESSAGE =
  'Feedback is not available for this activity.';

export default class QuizService {
  private isChecking = false;

  constructor(
    private readonly quiz: Quiz,
    private readonly block: BlockAPI,
    private readonly factory: QuizDomFactory,
    private readonly config?: ActivityBlockConfig,
  ) {}

  private correctAnswer(alternative: Alternative): AlternativeStatus {
    this.config?.callbacks.onCorrectAnswer?.(alternative.toJson());
    const item = this.block.holder.querySelector(
      `#${mountAlternativeId(alternative.getId())}`,
    );

    if (item) item.classList.add('alternative-correct');

    return 'correct';
  }

  private resetClassnames() {
    this.block.holder.querySelectorAll('.alternative-item').forEach(item => {
      item.classList.remove('alternative-correct', 'alternative-incorrect');
    });
  }

  private incorrectAnswer(alternative: Alternative): AlternativeStatus {
    this.config?.callbacks.onIncorrectAnswer?.(alternative.toJson());
    const item = this.block.holder.querySelector(
      `#${mountAlternativeId(alternative.getId())}`,
    );
    if (item) item.classList.add('alternative-incorrect');

    return 'incorrect';
  }
  public shouldBlockRetries() {
    return !this.config?.allowRetries && this.quiz.isDone();
  }

  private updateQuiz(alternative: Alternative, status: AlternativeStatus) {
    this.quiz.updateAlternativeStatus(alternative.getId(), status);
    this.quiz.setDone(true);
    return this.quiz.toJson();
  }

  private markAnswer(alternative: Alternative, isCorrect: boolean) {
    return isCorrect
      ? this.correctAnswer(alternative)
      : this.incorrectAnswer(alternative);
  }

  private async validateAnswer(
    alternative: Alternative,
  ): Promise<AlternativeStatus> {
    const check = await this.config?.callbacks.onCheckAnswer?.(
      alternative.toJson(),
      this.quiz.toJson(),
    );
    if (check?.status === 'checked')
      return this.markAnswer(alternative, check.isCorrect);
    if (check?.status === 'unavailable') {
      this.showMessage(FEEDBACK_UNAVAILABLE_MESSAGE, 'info');
      return 'empty';
    }
    const answer = await this.config?.callbacks.getAnswer?.(this.quiz.toJson());
    return this.markAnswer(alternative, answer?.id === alternative.getId());
  }

  private radios() {
    return this.block.holder.querySelectorAll<HTMLInputElement>(
      '.alternative-radio',
    );
  }

  private blockRetries() {
    if (!this.config?.allowRetries) {
      this.radios().forEach(radio => {
        radio.setAttribute('disabled', 'disabled');
      });
    }
  }

  private setChecking(isChecking: boolean) {
    this.isChecking = isChecking;
    this.block.holder
      .querySelector('.alternatives-container')
      ?.classList.toggle('alternatives-loading', isChecking);
    this.block.holder.setAttribute('aria-busy', String(isChecking));
    this.radios().forEach(radio => {
      if (isChecking) radio.setAttribute('disabled', 'disabled');
      else radio.removeAttribute('disabled');
    });
  }

  private clearMessage() {
    this.block.holder
      .querySelectorAll('.quiz-message')
      .forEach(message => message.remove());
  }

  private showMessage(message: string, variant: QuizMessageVariant) {
    const container =
      this.block.holder.querySelector('.quiz-container') ?? this.block.holder;
    container.appendChild(
      this.factory.createMessage(this.factory.i18n.t(message), variant),
    );
  }

  private uncheckAlternatives() {
    this.radios().forEach(radio => {
      radio.checked = false;
    });
  }

  onAnswer = async (alternative: Alternative, persist: boolean = false) => {
    if (this.isChecking) return;
    this.resetClassnames();
    this.clearMessage();
    this.setChecking(true);
    try {
      const result = await this.validateAnswer(alternative);
      this.setChecking(false);
      this.blockRetries();
      if (persist && result !== 'empty')
        return this.updateQuiz(alternative, result);
    } catch {
      this.setChecking(false);
      this.uncheckAlternatives();
      this.showMessage(CHECK_ANSWER_ERROR_MESSAGE, 'error');
    }
  };
}
